import { useFreeGiftInteraction } from '@/components/blocks/FreeGiftV2/context/FreeGiftInteractionContext';
import { useFreeGiftPromotion } from '@/components/blocks/FreeGiftV2/context/FreeGiftPromotionContext';
import { PurchaseBlock } from '@/components/blocks/FreeGiftV2/parts/PurchaseBlock';
import { RewardBlock } from '@/components/blocks/FreeGiftV2/parts/RewardBlock';
import { PromotionBlockSx } from '@/components/blocks/FreeGiftV2/styles/freeGift';
import { PurchaseGroup, RewardGroup, SkuQuantity } from '@/components/blocks/FreeGiftV2/types';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { Box, useTheme } from '@mui/material';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

const getPurchaseElementCount = (
	purchase: PurchaseGroup,
	selectedPurchaseSkus: SkuQuantity[],
	purchaseQty: number,
	othersQty: number,
	currentPartNumber: string
) => {
	const hasMultipleNoCombinableOptions =
		!Boolean(purchase?.combinable) && (purchase?.purchases?.length ?? 0) > 1;
	const isCurrentPartInPurchase = (purchase?.purchases ?? []).some((group) =>
		group.triggerSKUs.some((sku) => sku.sku === currentPartNumber)
	);
	const orderedPurchaseSkus =
		hasMultipleNoCombinableOptions && selectedPurchaseSkus.length === 0 && isCurrentPartInPurchase
			? [{ sku: currentPartNumber, quantity: 1 }]
			: selectedPurchaseSkus;
	const totalSelectedPurchaseQty = selectedPurchaseSkus.reduce((sum, sku) => sum + sku.quantity, 0);
	const missingPurchaseQty = purchaseQty + othersQty - totalSelectedPurchaseQty;

	return (
		Math.min(orderedPurchaseSkus.length, 3) +
		(missingPurchaseQty > 0 && orderedPurchaseSkus.length > 0 ? 1 : 0) +
		(missingPurchaseQty > 0 ? 1 : 0) +
		(orderedPurchaseSkus.length > 3 ? 1 : 0)
	);
};

const getRewardElementCount = (
	reward: RewardGroup,
	selectedRewardSkus: SkuQuantity[],
	rewardsQty: number
) => {
	const isRewardCombinable = reward?.combinable;
	const userHasSelectedGifts = selectedRewardSkus.length > 0;
	const totalSelectedRewardQty = selectedRewardSkus.reduce((sum, sku) => sum + sku.quantity, 0);
	const noCombinableGiftQuantities = !isRewardCombinable
		? (reward?.rewards ?? []).map((group) => group.quantity)
		: [];
	const missingGiftQty = isRewardCombinable
		? rewardsQty - totalSelectedRewardQty
		: userHasSelectedGifts
		? 0
		: noCombinableGiftQuantities[0] ?? 0;

	return (
		Math.min(selectedRewardSkus.length, 3) +
		(missingGiftQty > 0 && userHasSelectedGifts ? 1 : 0) +
		(missingGiftQty > 0 ? 1 : 0) +
		(selectedRewardSkus.length > 3 ? 1 : 0)
	);
};

export const PromotionBlock = () => {
	const theme = useTheme();
	const { purchase, purchaseQty, othersQty, reward, rewardsQty, currentPartNumber } =
		useFreeGiftPromotion();
	const { selectedPurchaseSkus, selectedRewardSkus } = useFreeGiftInteraction();

	const triggerCount = purchaseQty + othersQty;
	const giftCount = rewardsQty;

	const stackOnXs = triggerCount > 1 || giftCount > 1;
	const stackOnSm = triggerCount > 1 && giftCount > 1;

	const purchaseElementCount = getPurchaseElementCount(
		purchase,
		selectedPurchaseSkus,
		purchaseQty,
		othersQty,
		currentPartNumber
	);
	const rewardElementCount = getRewardElementCount(reward, selectedRewardSkus, rewardsQty);

	const stackOnMd = purchaseElementCount > 2 || rewardElementCount > 2;

	return (
		<Box
			sx={{
				...PromotionBlockSx.container,
				flexDirection: {
					xs: stackOnXs ? 'column' : 'row',
					sm: stackOnSm ? 'column' : 'row',
					md: stackOnMd ? 'column' : 'row',
					lg: stackOnMd ? 'column' : 'row',
					xl: 'row',
				},
			}}
		>
			<PurchaseBlock
				sx={{
					width: {
						md: stackOnMd ? '100%' : 'unset',
						lg: stackOnMd ? '100%' : 'unset',
						xl: 'unset',
					},
				}}
			/>
			<DynamicIcon
				name={MATERIAL_SYMBOL_ICONS.arrowForward}
				color={theme.palette.grey[500]}
				size="30px"
				sx={{
					display: {
						xs: stackOnXs ? 'none' : 'block',
						sm: stackOnSm ? 'none' : 'block',
						md: stackOnMd ? 'none' : 'block',
						lg: stackOnMd ? 'none' : 'block',
						xl: 'block',
					},
				}}
			/>
			<DynamicIcon
				name={MATERIAL_SYMBOL_ICONS.arrowDownward}
				color={theme.palette.grey[500]}
				size="30px"
				sx={{
					display: {
						xs: stackOnXs ? 'block' : 'none',
						sm: stackOnSm ? 'block' : 'none',
						md: stackOnMd ? 'block' : 'none',
						lg: stackOnMd ? 'block' : 'none',
						xl: 'none',
					},
				}}
			/>
			<RewardBlock
				sx={{
					width: {
						md: stackOnMd ? '100%' : 'unset',
						lg: stackOnMd ? '100%' : 'unset',
						xl: 'unset',
					},
				}}
			/>
		</Box>
	);
};
